import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
  Search,
  ShieldCheck,
  BadgeCheck,
  Loader2,
  Download,
  AlertCircle,
  User,
} from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import {
  GridBackground,
  NeonGlowOrbs,
} from "../components/GridBackground";
import api, { formatApiErrorDetail } from "../lib/api";

export default function StudentVerification() {
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [student, setStudent] = useState(null);
  const [error, setError] = useState("");

  const verifyStudent = async (searchValue) => {
    const value = searchValue.trim();

    if (!value) {
      setError(
        "Please enter an enrollment number, certificate ID, or student name."
      );
      return;
    }

    setLoading(true);
    setStudent(null);
    setError("");

    try {
      const res = await api.get("/verify", {
        params: { query: value },
      });

      setStudent(res.data);
    } catch (err) {
      setError(
        formatApiErrorDetail(err.response?.data?.detail) ||
          "Verification failed."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async (e) => {
    e?.preventDefault();
    await verifyStudent(query);
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const qrQuery = params.get("q");

    if (!qrQuery) return;

    const decodedQuery = qrQuery.trim();

    if (!decodedQuery) return;

    setQuery(decodedQuery);
    verifyStudent(decodedQuery);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDownload = () => {
    if (!student) return;

    window.open(
      `${api.defaults.baseURL}/marksheet/${student.enrollmentNumber}`,
      "_blank"
    );
  };

  return (
    <div
      data-testid="verification-page"
      className="min-h-screen bg-deep-black"
    >
      <Navbar />

      <section className="relative isolate overflow-hidden border-b border-white/5">
        <NeonGlowOrbs />
        <GridBackground />
        <div className="scanline absolute inset-0" />

        <div className="relative mx-auto max-w-4xl px-4 py-20 text-center sm:px-6 lg:px-8 lg:py-24">
          <div className="inline-flex items-center gap-2 rounded-full border border-neon-green/30 bg-neon-green/5 px-3 py-1 font-mono text-xs text-neon-green">
            <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-neon-green" />
            / marksheet verification
          </div>

          <h1 className="mt-5 font-display text-4xl font-bold tracking-tight text-white sm:text-5xl">
            Verify a{" "}
            <span className="text-neon-green text-glow">
              GITSI Marksheet
            </span>
          </h1>

          <p className="mx-auto mt-4 max-w-xl text-base text-white/65">
            Search by Enrollment Number, Marksheet ID, or Student Name.
          </p>

          <form
            onSubmit={handleSearch}
            className="mx-auto mt-8 flex max-w-2xl flex-col gap-3 sm:flex-row"
          >
            <div className="relative flex-1">
              <Search
                size={18}
                className="absolute left-4 top-1/2 -translate-y-1/2 text-neon-green/80"
              />

              <input
                data-testid="verify-search-input"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="e.g. GITDL11201234 or John Doe"
                className="input-dark pl-11 font-mono text-sm"
              />
            </div>

            <button
              type="submit"
              data-testid="verify-search-btn"
              disabled={loading}
              className="btn-neon disabled:opacity-60"
            >
              {loading ? (
                <Loader2
                  className="mr-2 animate-spin"
                  size={18}
                />
              ) : (
                <ShieldCheck
                  size={18}
                  className="mr-2"
                />
              )}

              {loading ? "Verifying..." : "Verify"}
            </button>
          </form>
        </div>
      </section>

      <section className="relative py-16">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          {error && (
            <motion.div
              data-testid="verify-error"
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              className="flex items-center gap-3 rounded-xl border border-soft-red/30 bg-soft-red/5 p-4 text-soft-red"
            >
              <AlertCircle size={18} />

              <p className="text-sm">
                {error}
              </p>
            </motion.div>
          )}

          {student && (
            <motion.div
              data-testid="verify-student-card"
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="relative overflow-hidden rounded-3xl border border-neon-green/30 bg-graphite/80 p-1 shadow-neon"
            >
              <div className="rounded-[22px] bg-deep-black/80 p-6 md:p-8">
                <div className="flex flex-col items-start gap-6 md:flex-row">
                  <div className="relative shrink-0">
                    <div className="absolute -inset-1 rounded-2xl bg-neon-green/20 blur-md" />

                    <div className="relative h-32 w-32 overflow-hidden rounded-2xl border border-neon-green/40 bg-graphite">
                      {student.studentPhoto ? (
                        <img
                          src={student.studentPhoto}
                          alt={student.name}
                          className="h-full w-full object-cover"
                        />
                      ) : (
                        <div className="grid h-full w-full place-items-center text-white/40">
                          <User size={42} />
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="inline-flex items-center gap-1 rounded-full border border-neon-green/40 bg-neon-green/10 px-2.5 py-1 text-xs font-semibold text-neon-green">
                        <BadgeCheck size={12} />
                        {student.status || "Verified"}
                      </span>

                      <span className="font-mono text-xs text-white/50">
                        {student.certificateId}
                      </span>
                    </div>

                    <h2 className="mt-2 font-display text-2xl font-bold text-white">
                      {student.name}
                    </h2>

                    <p className="text-sm text-white/60">
                      S/o, D/o {student.fatherName}
                    </p>

                    <div className="mt-4 grid gap-3 sm:grid-cols-2">
                      <Field
                        label="Course"
                        value={student.courseName}
                      />

                      <Field
                        label="Duration"
                        value={student.duration}
                        mono
                      />

                      <Field
                        label="Enrollment No"
                        value={student.enrollmentNumber}
                        mono
                        highlight
                      />

                      <Field
                        label="Date of Birth"
                        value={student.dob}
                        mono
                      />

                      <Field
                        label="Admission Date"
                        value={student.admissionDate}
                        mono
                      />

                      <Field
                        label="Completion Date"
                        value={student.completionDate}
                        mono
                      />

                      <Field
                        label="Marks"
                        value={`${student.marks || 0}/${
                          student.maxMarks || 100
                        }`}
                        mono
                      />

                      <Field
                        label="Grade / %"
                        value={`${student.grade || "-"} (${
                          student.percentage || 0
                        }%)`}
                        mono
                        highlight
                      />
                    </div>

                    <div className="mt-6 flex flex-wrap gap-3">
                      <button
                        data-testid="verify-download-btn"
                        onClick={handleDownload}
                        className="btn-neon !px-4 !py-2 text-sm"
                      >
                        <Download
                          size={16}
                          className="mr-2"
                        />
                        Download Marksheet
                      </button>

                      <div className="terminal !py-2 !text-xs">
                        $ marksheet.valid ==={" "}
                        <span className="text-neon-green">
                          true
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          )}

          {!student && !error && (
            <div className="rounded-2xl border border-dashed border-white/10 p-10 text-center">
              <p className="font-mono text-sm text-white/40">
                // awaiting input...
              </p>

              <p className="mt-2 text-white/60">
                Enter a student identifier above to verify the marksheet.
              </p>
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
}

function Field({
  label,
  value,
  mono,
  highlight,
}) {
  return (
    <div className="rounded-xl border border-white/5 bg-white/[0.02] px-4 py-3">
      <div className="text-[10px] uppercase tracking-widest text-white/40">
        {label}
      </div>

      <div
        className={`mt-1 text-sm ${
          mono ? "font-mono" : ""
        } ${
          highlight
            ? "text-neon-green"
            : "text-white"
        }`}
      >
        {value || "-"}
      </div>
    </div>
  );
}